import axios from 'axios'
import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import {
  JWT_KEY,
  clearSession,
  redirectToLogin,
  refreshTokens,
  sessionAgeExceeded,
} from '../auth/session'
import { ApiError } from './ApiError'

// Marca interna para no reintentar dos veces la misma request.
type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor: attach Bearer token
apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem(JWT_KEY)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: unknown) => Promise.reject(error instanceof Error ? error : new Error(String(error))),
)

// Response interceptor: handle auth errors globally
apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    const axiosErr = error as AxiosError<{ message?: string; error?: string }>

    // 401 = access token vencido (dura 60 min). Mientras la sesión siga dentro
    // de las 12 h, se renueva en silencio y se reintenta la request: el operador
    // no se entera.
    //
    // Antes esto navegaba directo a `VITE_COGNITO_LOGIN_URL`, que solo trae
    // `client_id` — sin response_type, redirect_uri ni scope. Cada hora el
    // operador terminaba en la pantalla "Required parameters missing" de
    // Cognito. El único camino válido al login es redirectToLogin() (PKCE).
    if (axiosErr.response?.status === 401) {
      // Con el bypass de dev (VITE_DEV_USER) no hay tokens de Cognito, así que
      // el primer 401 mandaba al login de Google y el bypass no servía para
      // nada: no se podía ver una sola pantalla del portal sin una sesión real.
      // En dev el 401 se devuelve como error normal y la pantalla muestra su
      // estado de error, que es lo que se quiere revisar.
      if (import.meta.env.VITE_DEV_USER) {
        return Promise.reject(new ApiError(
          'Sin sesión real (VITE_DEV_USER activo): el backend rechazó la llamada.',
          401, 'dev_sin_token',
        ))
      }

      const original = axiosErr.config as RetriableConfig | undefined

      // Salió sin token: alguien cerró la sesión (inactividad, «Salir») con la
      // pantalla pidiendo. Ir al login acá deshace ese cierre: Cognito devuelve
      // un código sin preguntar y la persona vuelve a entrar sola.
      if (!original?.headers?.Authorization) {
        return Promise.reject(new ApiError('Sin sesión.', 401, 'sin_sesion'))
      }

      if (!original._retried && !sessionAgeExceeded()) {
        original._retried = true
        const renewed = await refreshTokens()
        if (renewed) {
          const token = sessionStorage.getItem(JWT_KEY)
          if (token) original.headers.Authorization = `Bearer ${token}`
          return apiClient(original)
        }
      }

      // Sesión de más de 12 h, sin refresh token, o Cognito lo rechazó.
      clearSession()
      void redirectToLogin()
      return Promise.reject(new Error('Tu sesión venció. Te llevamos al login.'))
    }

    // El status y el código de negocio se conservan en el error. Antes se
    // rechazaba con un Error plano y se perdía `response`: los `catch` que
    // chequeaban `err.response.status === 409` para detectar la ventana de 24 h
    // cerrada nunca entraban, y el asesor veía "no se pudo enviar" sin el
    // botón de plantilla que resuelve el caso.
    const status = axiosErr.response?.status ?? 0
    const data = axiosErr.response?.data
    const code = typeof data?.error === 'string' ? data.error : ''

    if (status === 403) {
      return Promise.reject(new ApiError(
        data?.message ?? 'No tienes permiso para esta acción.', 403, code,
      ))
    }

    const message = data?.message ?? axiosErr.message ?? 'Error de red'
    return Promise.reject(new ApiError(message, status, code))
  },
)

export default apiClient
